import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { Shell } from "@/components/Shell";
import { MultiChipGroup, ChipGroup, Section } from "@/components/fields";
import { MARKET_FEEDBACK, DAY_STATUS, MGMT_SUPPORT } from "@/constants";
import { money, getGps } from "@/lib/format";
import { shareDayReportPdf } from "@/lib/pdf";
import { toast } from "sonner";
import { CaretLeft, CheckCircle, WhatsappLogo } from "@phosphor-icons/react";

export default function EndDay() {
  const { user, session, setSession, loadSession } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [visits, setVisits] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [dayStatus, setDayStatus] = useState("");
  const [support, setSupport] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [endKm, setEndKm] = useState("");
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/summary/today").then(({ data }) => setSummary(data));
    api.get("/visits", { params: { date: new Date().toISOString().slice(0, 10) } }).then(({ data }) => setVisits(data));
  }, []);

  const submit = async () => {
    setSaving(true);
    const gps = await getGps();
    try {
      await api.post("/session/end", {
        market_feedback: feedback, day_status: dayStatus, support_required: support,
        remarks, end_km: endKm ? Number(endKm) : null, gps,
      });
      await loadSession();
      setDone(true);
    } catch (e) { toast.error("Failed to submit"); }
    finally { setSaving(false); }
  };

  const enOf = (opts, v) => (opts.find((o) => o.value === v) || {}).en || v;
  const shareReport = async () => {
    const r = await shareDayReportPdf({
      mrName: user?.name, employeeId: user?.employee_id, hq: user?.headquarters,
      area: session?.working_area, date: new Date().toLocaleDateString("en-IN"),
      startTime: session?.start_time, endTime: new Date().toISOString(),
      summary, feedback: feedback.map((v) => enOf(MARKET_FEEDBACK, v)),
      dayStatus: enOf(DAY_STATUS, dayStatus), support: support.map((v) => enOf(MGMT_SUPPORT, v)),
      remarks, endKm,
      visits: visits.map((v) => ({ name: v.contact_name, time: v.visit_time, type: v.contact_type, status: v.call_status || v.response || "" })),
    });
    if (r === "downloaded" || r === "shared") toast.success(t("share_day_report"));
  };

  if (done) {
    return (
      <Shell title={t("end_my_day")} nav={false}>
        <div className="flex flex-col items-center justify-center text-center py-20 gap-4">
          <CheckCircle size={72} weight="fill" className="text-emerald-500" />
          <p className="font-head font-bold text-lg text-slate-800" data-testid="submit-success">{t("submit_success")}</p>
          <button onClick={shareReport} data-testid="share-day-report" className="mt-2 px-6 min-h-[48px] rounded-lg border-2 border-emerald-500 text-emerald-600 font-semibold flex items-center gap-2">
            <WhatsappLogo size={20} weight="fill" /> {t("share_day_report")}
          </button>
          <button onClick={() => nav("/")} className="px-6 min-h-[48px] rounded-lg bg-slate-800 text-white font-semibold" data-testid="report-home-btn">{t("home")}</button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell title={t("end_my_day")} nav={false}
      back={<button onClick={() => nav("/")} data-testid="back-btn"><CaretLeft size={22} /></button>}>
      <div className="space-y-4">
        {summary && (
          <div className="rounded-xl bg-slate-800 text-white p-4">
            <h3 className="text-xs uppercase tracking-wide text-slate-300 mb-3">{t("report_preview")}</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>{t("visits")}: <b>{summary.total_visits}</b></div>
              <div>{t("doctor_visit")}: <b>{summary.total_doctor_visits}</b></div>
              <div>{t("order_value")}: <b>{money(summary.order_value)}</b></div>
              <div>{t("collection")}: <b>{money(summary.collection)}</b></div>
              <div>{t("expenses")}: <b>{money(summary.expense_total)}</b></div>
              <div>{t("samples")}: <b>{summary.samples}</b></div>
            </div>
          </div>
        )}

        <Section title={t("market_feedback")} testid="section-feedback">
          <MultiChipGroup options={MARKET_FEEDBACK} value={feedback} onChange={setFeedback} testid="market-feedback" />
        </Section>
        <Section title={t("day_status")} testid="section-daystatus">
          <ChipGroup options={DAY_STATUS} value={dayStatus} onChange={setDayStatus} testid="day-status" />
        </Section>
        <Section title={t("management_support")} testid="section-mgmt">
          <MultiChipGroup options={MGMT_SUPPORT} value={support} onChange={setSupport} testid="mgmt-support" />
        </Section>
        <Section title={`${t("end_km")} · ${t("remarks")}`} testid="section-endkm">
          <input data-testid="end-km" value={endKm} onChange={(e) => setEndKm(e.target.value)} inputMode="numeric" placeholder="KM" className="w-full min-h-[48px] rounded-lg border border-slate-200 px-3 mb-3" />
          <textarea data-testid="remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} className="w-full rounded-lg border border-slate-200 p-3" />
        </Section>

        <button onClick={submit} disabled={saving} data-testid="submit-report" className="w-full min-h-[56px] rounded-xl bg-emerald-600 text-white font-head font-bold text-lg disabled:opacity-40 active:scale-[0.98]">
          {t("submit_report")}
        </button>
      </div>
    </Shell>
  );
}
